/**
 * LibraryListScreen — `/library`, the knowledge base as a ruled index.
 *
 * WHY IT LOOKS LIKE THIS. The previous build rendered the index as a column of
 * identical cards, each one [tinted book chip + bold title + clock + minutes +
 * chevron]. That is the single most generated-looking arrangement in the app:
 * the icon chip repeats fifteen times and carries no information, the card
 * borders add fourteen horizontal lines that say nothing, and the one fact that
 * differs between rows — the title — competes with all of it.
 *
 * The counter-slip direction (index.html) asks for the opposite: DRAWN armature
 * and a LABEL GRID. So the index is now literally an index — two named columns
 * (`Статья` / `Чтение`) over hairline-ruled rows, the reading time set in
 * `.g-data` under a stretched column rule so the figures scan as one vertical
 * column. Nothing decorative repeats; the rules are the only chrome and they
 * are load-bearing.
 *
 * WAYFINDING. This is a read-mode surface, so finding the right article beats
 * expression. Above the index: a filter (appears only once the list is long
 * enough to need one — `FILTER_THRESHOLD`) and a live matched/total count as a
 * real figure. Hovering or focusing a row prefetches that article, so opening
 * one from a bazaar on a slow connection is usually instant.
 *
 * WHAT WE CANNOT SHOW. `GET /kb/articles` returns only `{slug, title, lang,
 * reading_time_min}` — there is no summary/excerpt field, so a row cannot say
 * what the article answers beyond its own title. Fabricating one client-side
 * would violate the grounding principle. Adding `summary` to `KbArticleSummary`
 * is the backend change this screen wants; it is flagged in the handover, not
 * faked here.
 *
 * STATES. Loading is the same ruled grid in skeleton form (never a different
 * layout that reflows on arrival). Empty, no-match and error are all the same
 * left-aligned ruled slip — never the centred icon-disc + card-grid template.
 * A retry that fails again raises a `notify.error` naming the actual failure
 * with the retry attached, because the inline state alone does not visibly
 * change when the second attempt dies.
 *
 * All copy is `library` i18n (ru/ky/en, ADR-028); all colour/space/type comes
 * from `--g-*` tokens; icons are lucide at the shared `iconProps` weight.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Search, X } from "lucide-react";

import { Button, GMotif, Skeleton } from "@garden/ui";

import { Stagger, StaggerItem } from "@/components/motion";
import { getArticle, type KbArticleSummary } from "@/lib/garden-api";
import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";

import {
  libraryQueryKeys,
  registerLibraryI18n,
  resolveKbLanguage,
  useArticleList,
} from "./api";

/** Route path for one article (keeps the link target in one place). */
export function articlePath(slug: string): string {
  return `/library/${slug}`;
}

/**
 * Below this many articles the filter is chrome, not wayfinding — the whole
 * index fits on one screen and a search box just costs a tap target.
 */
const FILTER_THRESHOLD = 6;

/** Page shell: the reading column on the ground, generous top space. */
function LibraryShell({ children }: { children: ReactElement }): ReactElement {
  return (
    <div data-testid="library-list-screen" className="min-h-full bg-[var(--g-ground)]">
      <div className="mx-auto max-w-[62rem] px-5 pb-24 pt-7 md:px-8 md:pb-20 md:pt-12">
        {children}
      </div>
    </div>
  );
}

/** Screen title + one line of promise. Present in every state. */
function LibraryHeader(): ReactElement {
  const { t } = useTranslation("library");
  return (
    <header>
      <h1 className="font-display m-0 text-[28px] leading-[1.08] text-foreground md:text-[44px]">
        {t("listTitle")}
      </h1>
      <p className="mt-3 max-w-[46ch] text-[15px] leading-[1.6] text-[var(--g-ink-2)] md:mt-4 md:text-[16.5px]">
        {t("listSubtitle")}
      </p>
    </header>
  );
}

/**
 * The two column labels over the index. This is the direction's "visible
 * armature": the grid that orders every row is named and drawn, not implied by
 * whitespace. The measure column carries the same left rule the rows do, so the
 * vertical line runs unbroken from the header through the last row.
 */
function IndexHeaderRow(): ReactElement {
  const { t } = useTranslation("library");
  return (
    <div className="mt-6 grid grid-cols-[minmax(0,1fr)_auto] items-stretch gap-x-6 border-b border-[var(--g-rule-strong)] md:mt-8 md:grid-cols-[minmax(0,1fr)_6rem_1.25rem]">
      {/* At 390px there is only one column, so the column NAMES are noise —
          the strong rule alone carries the armature and the count above it
          already says how many articles there are. */}
      <span className="hidden self-end pb-2 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)] md:block">
        {t("labelArticle")}
      </span>
      <span className="hidden self-stretch pb-2 pt-2 text-right text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)] md:block md:border-l md:border-[var(--g-rule)]">
        {t("labelRead")}
      </span>
      <span aria-hidden="true" className="hidden md:block" />
    </div>
  );
}

/**
 * One index row. A real `<Link>` (not a clickable div) so middle-click, copy
 * link and keyboard all behave; the reading time sits in a ruled column and the
 * minutes figure is `.g-data` while its unit stays in body type — the figure is
 * the data, the word is a label.
 */
function ArticleRow({
  article,
  onPrefetch,
}: {
  article: KbArticleSummary;
  onPrefetch: (slug: string) => void;
}): ReactElement {
  const { t } = useTranslation("library");
  const minutes = article.reading_time_min ?? 1;
  return (
    <Link
      to={articlePath(article.slug)}
      data-testid={`library-article-${article.slug}`}
      onMouseEnter={() => onPrefetch(article.slug)}
      onFocus={() => onPrefetch(article.slug)}
      className="group grid grid-cols-[minmax(0,1fr)_auto] items-stretch gap-x-6 transition-colors hover:bg-[var(--g-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:grid-cols-[minmax(0,1fr)_6rem_1.25rem]"
    >
      {/* Vertical padding lives on the CELLS, not on the row, so the measure
          column's left rule spans the whole row height and the rules of
          consecutive rows join into one drawn column line. */}
      <span className="col-start-1 row-start-1 min-w-0 pt-4 text-[16.5px] font-semibold leading-[1.35] text-foreground md:py-5 md:text-[19px]">
        {article.title}
      </span>
      <span className="col-start-1 row-start-2 flex items-baseline gap-1.5 pb-4 pt-1 md:col-start-2 md:row-start-1 md:items-center md:justify-end md:self-stretch md:border-l md:border-[var(--g-rule)] md:py-5">
        <span className="g-data text-[14px] font-medium text-[var(--g-figure)] md:text-[15px]">
          {minutes}
        </span>
        <span className="text-[12.5px] text-[var(--g-ink-2)]">{t("minUnit")}</span>
      </span>
      <ArrowRight
        {...iconProps("md")}
        className="col-start-2 row-start-1 row-span-2 self-center justify-self-end text-[var(--g-ink-2)] transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 md:col-start-3 md:row-span-1"
      />
    </Link>
  );
}

/**
 * The shared shape for empty / no-match / error. A ruled slip: a strong top
 * rule, the headline in the display voice, one line of plain explanation and
 * exactly one action. Left-aligned on purpose — a centred icon disc over a card
 * grid is the template this build refuses.
 */
function LibrarySlip({
  testId,
  role,
  title,
  body,
  action,
  motif = false,
}: {
  testId: string;
  role: "status" | "alert";
  title: string;
  body: string;
  action: ReactElement;
  motif?: boolean;
}): ReactElement {
  return (
    <div
      data-testid={testId}
      role={role}
      className="relative mt-6 overflow-hidden border-t border-[var(--g-rule-strong)] pb-10 pt-9 md:mt-8 md:pb-14 md:pt-12"
    >
      {motif && (
        <GMotif
          tone="primary"
          opacity={0.16}
          className="pointer-events-none absolute -right-6 -top-4 h-40 w-56 md:h-52 md:w-72"
        />
      )}
      <div className="relative max-w-[42ch]">
        <p className="font-display m-0 text-[21px] leading-[1.2] text-foreground md:text-[26px]">
          {title}
        </p>
        <p className="mt-3 text-[15px] leading-[1.65] text-[var(--g-ink-2)]">{body}</p>
        <div className="mt-6">{action}</div>
      </div>
    </div>
  );
}

/**
 * Loading placeholder. Deliberately the SAME grid as the loaded index (ruled
 * rows, measure column on the right) so nothing jumps when the data lands.
 */
function LibraryListSkeleton(): ReactElement {
  const widths = ["78%", "62%", "84%", "55%", "70%", "66%"];
  return (
    <div data-testid="library-list-skeleton" aria-hidden="true" className="g-ruled">
      {widths.map((w, i) => (
        <div
          key={i}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 gap-y-2 py-4 md:grid-cols-[minmax(0,1fr)_6rem_1.25rem] md:py-5"
        >
          <div className="col-start-1 row-start-1">
            <Skeleton w={w} h={18} r={6} />
          </div>
          <div className="col-start-1 row-start-2 md:col-start-2 md:row-start-1 md:justify-self-end">
            <Skeleton w={44} h={13} r={6} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LibraryListScreen(): ReactElement {
  registerLibraryI18n();
  const { t, i18n } = useTranslation("library");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lang = resolveKbLanguage(i18n.language);
  const [query, setQuery] = useState("");

  const { data, isPending, isError, refetch } = useArticleList(lang);

  // Warm the reader's cache from the index. TanStack shares the key with
  // `useArticle`, so a hovered row usually opens with no spinner at all.
  const prefetch = (slug: string): void => {
    void queryClient.prefetchQuery({
      queryKey: libraryQueryKeys.article(slug, lang),
      queryFn: () => getArticle(slug, lang),
      staleTime: 5 * 60_000,
    });
  };

  // A retry that fails again looks like nothing happened — the inline error
  // state is already on screen. Name the failure and re-offer the recovery.
  function retry(): void {
    void refetch().then((result) => {
      if (result.isError) {
        notify.error(t("listErrorTitle"), {
          description: t("listErrorBody"),
          action: { label: t("retry"), onClick: retry },
          id: "library-list-error",
        });
      }
    });
  }

  const articles = data?.articles;
  const filtered = useMemo(() => {
    if (articles === undefined) return [];
    const needle = query.trim().toLocaleLowerCase();
    if (needle === "") return articles;
    return articles.filter((a) =>
      `${a.title} ${a.slug}`.toLocaleLowerCase().includes(needle),
    );
  }, [articles, query]);

  // `isPending` covers the first fetch; the `data === undefined` belt-and-
  // braces keeps the success branch below strictly typed.
  if (isPending || (data === undefined && !isError)) {
    return (
      <LibraryShell>
        <>
          <LibraryHeader />
          <IndexHeaderRow />
          <LibraryListSkeleton />
        </>
      </LibraryShell>
    );
  }

  if (isError || data === undefined) {
    return (
      <LibraryShell>
        <>
          <LibraryHeader />
          <LibrarySlip
            testId="library-list-error"
            role="alert"
            title={t("listErrorTitle")}
            body={t("listErrorBody")}
            action={
              <Button
                variant="outline"
                onClick={retry}
                data-testid="library-list-retry"
              >
                {t("retry")}
              </Button>
            }
          />
        </>
      </LibraryShell>
    );
  }

  const total = data.articles.length;

  if (total === 0) {
    return (
      <LibraryShell>
        <>
          <LibraryHeader />
          <LibrarySlip
            testId="library-list-empty"
            role="status"
            motif
            title={t("emptyTitle")}
            body={t("emptyBody")}
            action={
              <Button leading="sparkles" onClick={() => navigate("/chat")}>
                {t("askCta")}
              </Button>
            }
          />
        </>
      </LibraryShell>
    );
  }

  const showFilter = total >= FILTER_THRESHOLD;

  return (
    <LibraryShell>
      <>
        <LibraryHeader />

        <div
          className={
            "mt-7 flex flex-col gap-3 md:mt-10 md:flex-row md:items-center md:gap-6 " +
            (showFilter ? "md:justify-between" : "md:justify-end")
          }
        >
          {showFilter && (
            <label className="relative flex w-full items-center md:max-w-[22rem]">
              <span className="sr-only">{t("filterLabel")}</span>
              <Search
                {...iconProps("sm")}
                className="pointer-events-none absolute left-3.5 text-[var(--g-ink-2)]"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("filterPlaceholder")}
                data-testid="library-filter"
                className="h-12 w-full rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)] pl-10 pr-14 text-[15px] text-foreground placeholder:text-[var(--g-ink-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&::-webkit-search-cancel-button]:appearance-none"
              />
              {query !== "" && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t("filterClear")}
                  data-testid="library-filter-clear"
                  className="absolute right-0.5 grid size-11 place-items-center rounded-[var(--g-radius)] text-[var(--g-ink-2)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X {...iconProps("sm")} />
                </button>
              )}
            </label>
          )}
          <p
            className="flex shrink-0 items-baseline gap-2 text-[11px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]"
            data-testid="library-count"
          >
            {t("labelCount")}
            <span className="g-data text-[14px] font-medium normal-case tracking-normal text-[var(--g-figure)]">
              {filtered.length === total
                ? total
                : `${String(filtered.length)} / ${String(total)}`}
            </span>
          </p>
        </div>

        {/* No matches → no columns to name. Dropping the header row keeps the
            slip's own top rule the only line on the page. */}
        {filtered.length === 0 ? (
          <LibrarySlip
            testId="library-list-no-match"
            role="status"
            title={t("noMatchTitle")}
            body={t("noMatchBody")}
            action={
              <Button variant="outline" onClick={() => setQuery("")}>
                {t("filterClear")}
              </Button>
            }
          />
        ) : (
          <>
            <IndexHeaderRow />
            <Stagger className="g-ruled" data-testid="library-article-list">
              {filtered.map((article) => (
                <StaggerItem key={article.slug}>
                  <ArticleRow article={article} onPrefetch={prefetch} />
                </StaggerItem>
              ))}
            </Stagger>
          </>
        )}
      </>
    </LibraryShell>
  );
}
